/**
 * Signing in with an email and password typed on Studio's own sign-in page.
 *
 * The API makes WorkOS's password grant (it needs the API key) and answers
 * with the session's refresh token. AuthKit, in the dev mode Studio runs it
 * in, keeps its refresh token in localStorage under `workos:refresh-token:<client id>`
 * and the session's organization in sessionStorage under `workos-org-id:<client id>`;
 * writing both and reloading makes AuthKit start up by refreshing that
 * session, exactly as it does for one from its own redirect. Without dev mode
 * (a custom authentication domain) the refresh token lives in an HttpOnly
 * cookie instead, and this hand-off has to move to the API setting it.
 */
import { AUTH, IS_OIDC_BUILD } from './config';

const STUDIO_API_BASE_URL = import.meta.env.VITE_STUDIO_API_BASE_URL || 'http://localhost:8087';

/** Only a WorkOS build can take a password session; an OIDC build signs in on its issuer's page. */
export const PASSWORD_SIGN_IN_AVAILABLE = !IS_OIDC_BUILD && AUTH.provider === 'workos' && AUTH.configured;

export type PasswordSignInOutcome =
  | { kind: 'signed_in' }
  /** The organization signs in through SSO, or WorkOS wants a step only its hosted page does. */
  | { kind: 'redirect' }
  | { kind: 'wrong_credentials' }
  | { kind: 'too_many_attempts' }
  | { kind: 'failed' };

type Storage = Pick<globalThis.Storage, 'setItem'>;

/** Hand AuthKit the session the API started; it refreshes it on the next load. */
export function adoptWorkOSSession(
  session: { refreshToken: string; organizationId?: string },
  clientId: string,
  local: Storage = window.localStorage,
  perTab: Storage = window.sessionStorage,
): void {
  local.setItem(`workos:refresh-token:${clientId}`, session.refreshToken);
  if (session.organizationId) perTab.setItem(`workos-org-id:${clientId}`, session.organizationId);
}

export async function signInWithPassword(
  email: string,
  password: string,
  fetcher: typeof fetch = fetch,
  clientId: string = AUTH.clientId ?? '',
): Promise<PasswordSignInOutcome> {
  let response: Response;
  try {
    response = await fetcher(`${STUDIO_API_BASE_URL}/api/studio/auth/password`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password }),
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed' };
  }
  const body = (await response.json().catch(() => ({}))) as { code?: unknown; refreshToken?: unknown; organizationId?: unknown };
  if (response.ok && typeof body.refreshToken === 'string' && body.refreshToken && clientId) {
    adoptWorkOSSession(
      { refreshToken: body.refreshToken, organizationId: typeof body.organizationId === 'string' ? body.organizationId : undefined },
      clientId,
    );
    return { kind: 'signed_in' };
  }
  if (body.code === 'sso_required' || body.code === 'continue_hosted' || body.code === 'password_sign_in_disabled') return { kind: 'redirect' };
  if (body.code === 'wrong_credentials') return { kind: 'wrong_credentials' };
  if (body.code === 'too_many_attempts') return { kind: 'too_many_attempts' };
  return { kind: 'failed' };
}
